import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, CalendarClock, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../Button';
import { ReportMetadata, ServiceItem, UserProfile } from '../../types';
import { loadProjectById } from '../../services/cloudService';
import { getNormalizedServices } from '../../services/projectLv';
import { altCoveredQuantities } from '../../services/externalAdvances';
import { fetchInvoicesForProject } from '../../services/invoiceService';
import { BillingMode, establishedBillingModeByProject } from '../../services/billingMode';
import { flatKeyLabel, isFlatRateService } from '../../services/flatRateLogic';
import { FLAT_SCOPE_LABELS, FlatScopeChoice, PROJECT_FLAT_SCOPES } from '../../services/flatRateSetup';
import {
    FlatSwitchDraft,
    assessFlatSwitch,
    flatSwitchCumulativeHint,
} from '../../services/flatRateSwitchLogic';
import { switchProjectToFlatRate } from '../../services/flatRateSwitchService';
import { releaseServiceWaiver, waiveServiceQuantity } from '../../services/serviceWaiverService';
import { ServiceWaiverDialog } from './ServiceWaiverDialog';
import { getServiceLog } from '../../services/serviceJournal';
import type { ListProject } from '../../services/listProject';

interface Props {
    /**
     * Projektzeile der Buchhaltungsansicht — nur fuer id und Beschriftung.
     * Bewusst nur diese drei Felder: die Zeile ist ein Listenauszug, gerechnet
     * wird am frisch geladenen Projekt (`row`).
     */
    project: Pick<ListProject, 'id' | 'name' | 'project_number'>;
    currentUser: UserProfile | null;
    /** Ausweg 1 des Riegels: die Restmenge vorher abrechnen (Weg zur Rechnung). */
    onBillProject?: (projectId: string) => void;
    /** Nach erfolgreicher Umstellung: Ansicht schliessen und neu laden. */
    onDone: () => void;
    onClose: () => void;
}

/**
 * UMSTELLUNGS-ASSISTENT: ein laufendes Projekt von der Abrechnung je
 * Einzelposition auf die Monatspauschale umstellen.
 *
 * Duenne Komponente: alle Regeln (harter Riegel, Monatsverschluesse,
 * Monatsvorschlag, Kumulativ-Hinweis) liegen in flatRateSwitchLogic und sind
 * dort getestet; geschrieben wird ausschliesslich ueber
 * flatRateSwitchService. Hier stehen nur Eingaben, Anzeige und die beiden
 * Auswege des Riegels — der Verzicht laeuft ueber den BESTEHENDEN Dialog und
 * Schreibpfad (ServiceWaiverDialog/serviceWaiverService), damit es keinen
 * zweiten Weg auf dieselben Mengen gibt.
 *
 * Das Projekt wird beim Oeffnen FRISCH geladen (und nach jedem Verzicht
 * erneut): die Liste der Buchhaltungsansicht ist ein Abzug vom letzten
 * Refresh, und der Riegel rechnet gegen quantityUsed/quantityBilled.
 */
export const FlatRateSwitchAssistant: React.FC<Props> = ({
    project,
    currentUser,
    onBillProject,
    onDone,
    onClose,
}) => {
    const [row, setRow] = useState<any | null>(null);
    const [ladeFehler, setLadeFehler] = useState<string | null>(null);
    const [billingMode, setBillingMode] = useState<BillingMode | null>(null);

    // Eingaben (Schritt 1): Stichtag, Umfang, Betrag, Monate. Der Umfang
    // startet LEER — keine Vorbelegung, die Deckung ist eine ausdrueckliche
    // Entscheidung (dieselbe Linie wie im Anlage-Wizard).
    const heute = useMemo(() => new Date().toISOString().slice(0, 10), []);
    const [stichtag, setStichtag] = useState(heute);
    const [scopeChoice, setScopeChoice] = useState<FlatScopeChoice>('');
    const [explicitIds, setExplicitIds] = useState<string[]>([]);
    const [betragText, setBetragText] = useState('');
    const [monateText, setMonateText] = useState('');
    const [lvPosition, setLvPosition] = useState('');
    const [titel, setTitel] = useState('Monatspauschale');

    const [waiverServiceId, setWaiverServiceId] = useState<string | null>(null);
    const [laeuft, setLaeuft] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [erfolg, setErfolg] = useState<string | null>(null);

    const ladeProjekt = async () => {
        try {
            const fresh = await loadProjectById(project.id);
            if (!fresh) { setLadeFehler('Projekt konnte nicht geladen werden.'); return; }
            setRow(fresh);
        } catch (e: any) {
            setLadeFehler(`Projekt konnte nicht geladen werden: ${e?.message || e}`);
        }
    };

    useEffect(() => {
        ladeProjekt();
        // Abrechnungsmodell aus den AUSGESTELLTEN Belegen — dieselbe Regel wie
        // Rechnungsansicht und Rechnungslauf (billingMode.ts). Ein Ladefehler
        // laesst den Hinweis weg, er erzwingt nichts.
        fetchInvoicesForProject(project.id)
            .then((invoices) => setBillingMode(establishedBillingModeByProject(invoices).get(project.id) ?? null))
            .catch(() => setBillingMode(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project.id]);

    const meta = row?.data?.metadata as ReportMetadata | undefined;
    const globalData = meta?.globalData;
    const services = useMemo<ServiceItem[]>(() => (row ? getNormalizedServices(row) : []), [row]);
    // Bewusst OHNE useMemo: die Listen sind klein, und eine falsche
    // Abhaengigkeitsliste waere hier teurer als die Neuberechnung.
    const serviceLog = getServiceLog({ metadata: meta });
    const altCovered = altCoveredQuantities(serviceLog);

    const betrag = Number((betragText || '').replace(',', '.'));
    const monate = monateText.trim() === '' ? 0 : Number((monateText || '').replace(',', '.'));
    const draft: FlatSwitchDraft | null = scopeChoice === ''
        ? null
        : {
            stichtag,
            scope: scopeChoice,
            serviceIds: scopeChoice === 'explicit' ? explicitIds : undefined,
            monthlyAmount: betrag,
            monthsCount: monate,
            lvPosition,
            title: titel,
        };

    const assessment = row && draft
        ? assessFlatSwitch(
            {
                services,
                altCovered,
                startDate: globalData?.startDate,
                endDate: globalData?.endDate,
                today: heute,
                // Der Riegel zaehlt nur, was vor dem Stichtagsmonat erbracht ist.
                serviceLog,
            },
            draft,
        )
        : null;

    const kumulativHinweis = flatSwitchCumulativeHint(billingMode);
    const auswaehlbar = services.filter((s) => !isFlatRateService(s) && s.assignment !== 'flatrate');

    const umstellen = async () => {
        if (!draft) return;
        setLaeuft(true);
        setFehler(null);
        const ergebnis = await switchProjectToFlatRate({ projectId: project.id, draft, user: currentUser });
        setLaeuft(false);
        if (ergebnis.ok) setErfolg(ergebnis.message);
        else setFehler(ergebnis.message);
    };

    // Ausweg 2 des Riegels: Verzicht ueber den bestehenden Pfad, danach frisch
    // laden — der Riegel soll die Wirkung sofort sehen.
    const handleWaive = async (quantity: number, reason: string, entryIds?: string[]) => {
        if (!waiverServiceId) return { ok: false, message: 'Keine Position gewählt.' };
        const ergebnis = await waiveServiceQuantity({ projectId: project.id, serviceId: waiverServiceId, quantity, entryIds, reason, user: currentUser });
        if (ergebnis.ok) await ladeProjekt();
        return ergebnis;
    };
    const handleRelease = async (reason: string) => {
        if (!waiverServiceId) return { ok: false, message: 'Keine Position gewählt.' };
        const ergebnis = await releaseServiceWaiver({ projectId: project.id, serviceId: waiverServiceId, reason, user: currentUser });
        if (ergebnis.ok) await ladeProjekt();
        return ergebnis;
    };

    const fmtEur = (n: number) => `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
    const projektName = project?.name || project?.project_number || '';

    return (
        <div className="fixed inset-0 z-[160] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-3xl shadow-2xl animate-scale-in max-h-[90vh] overflow-y-auto">
                <div className="flex items-start gap-4 mb-4">
                    <div className="bg-indigo-50 p-3 rounded-full"><CalendarClock className="w-6 h-6 text-indigo-600" /></div>
                    <div className="min-w-0">
                        <h3 className="font-bold text-lg text-slate-800">Auf Monatspauschale umstellen</h3>
                        <p className="text-sm text-slate-600 mt-1 truncate" title={projektName}>{projektName}</p>
                    </div>
                </div>

                {erfolg ? (
                    <>
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-4 flex gap-2">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                            <p className="text-sm text-emerald-900 leading-relaxed whitespace-pre-line">{erfolg}</p>
                        </div>
                        <div className="flex justify-end">
                            <Button variant="primary" onClick={onDone}>Schließen</Button>
                        </div>
                    </>
                ) : ladeFehler ? (
                    <>
                        <p className="text-sm text-rose-600 mb-4">{ladeFehler}</p>
                        <div className="flex justify-end"><Button variant="secondary" onClick={onClose}>Schließen</Button></div>
                    </>
                ) : !row ? (
                    <div className="flex items-center gap-2 text-slate-500 text-sm py-8 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" /> Projekt wird geladen …
                    </div>
                ) : (
                    <>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 mb-4 text-xs text-slate-600 leading-relaxed">
                            Die Pauschale wird eine <strong>eigene LV-Position</strong> (Einheit Monat).
                            Gedeckte Positionen laufen als Nachweis weiter, ihr Geldstand steht still.
                            Umgestellt wird erst, wenn an den zu deckenden Positionen <strong>nichts mehr offen</strong> ist —
                            die Deckung kennt kein Datum, unabgerechnete Altleistung verschwände sonst lautlos.
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Stichtag der Umstellung</label>
                                <input type="date" value={stichtag} max={heute} onChange={(e) => setStichtag(e.target.value)}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Pauschale deckt</label>
                                <select value={scopeChoice} onChange={(e) => setScopeChoice(e.target.value as FlatScopeChoice)}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                                    <option value="">Bitte wählen …</option>
                                    {PROJECT_FLAT_SCOPES.map((s) => <option key={s} value={s}>{FLAT_SCOPE_LABELS[s]}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Monatsbetrag (netto)</label>
                                <input type="text" inputMode="decimal" value={betragText} placeholder="z. B. 1200"
                                    onChange={(e) => setBetragText(e.target.value)}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Zahl der Monate</label>
                                <input type="text" inputMode="decimal" value={monateText} placeholder="0 = offen"
                                    onChange={(e) => setMonateText(e.target.value)}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">LV-Nr. (optional)</label>
                                <input type="text" value={lvPosition} onChange={(e) => setLvPosition(e.target.value)}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Bezeichnung</label>
                                <input type="text" value={titel} onChange={(e) => setTitel(e.target.value)}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                            </div>
                        </div>

                        {scopeChoice === 'explicit' && (
                            <div className="rounded-xl border border-slate-200 p-3 mb-4 max-h-40 overflow-y-auto">
                                <div className="text-xs font-bold uppercase text-slate-500 mb-2">Gedeckte Positionen wählen</div>
                                {auswaehlbar.map((s) => (
                                    <label key={s.id} className="flex items-center gap-2 text-sm text-slate-700 py-0.5">
                                        <input type="checkbox" checked={explicitIds.includes(s.id)}
                                            onChange={() => setExplicitIds((ids) => ids.includes(s.id) ? ids.filter((x) => x !== s.id) : [...ids, s.id])}
                                            className="w-4 h-4 rounded border-slate-300 text-indigo-600" />
                                        <span className="font-mono text-xs text-slate-400">{s.lvPosition}</span> {s.title}
                                    </label>
                                ))}
                                {auswaehlbar.length === 0 && <p className="text-xs text-slate-400 italic">Keine deckbaren Positionen.</p>}
                            </div>
                        )}

                        {assessment && assessment.rows.length > 0 && (
                            <div className="mb-4">
                                <div className="text-xs font-bold uppercase text-slate-500 mb-2">Offene Restmengen der zu deckenden Positionen</div>
                                <table className="w-full text-sm">
                                    <tbody>
                                        {assessment.rows.map(({ service: s, openQty, openAmount }) => (
                                            <tr key={s.id} className="border-t border-slate-100">
                                                <td className="py-1.5 pr-2 text-slate-700">
                                                    <span className="font-mono text-xs text-slate-400 mr-1">{s.lvPosition}</span>{s.title}
                                                </td>
                                                <td className={`py-1.5 pr-2 text-right font-mono ${openQty > 0 ? 'text-rose-600 font-bold' : 'text-emerald-600'}`}>
                                                    {openQty} {s.unit}
                                                </td>
                                                <td className="py-1.5 pr-2 text-right font-mono text-slate-500 whitespace-nowrap">{openQty > 0 ? fmtEur(openAmount) : '—'}</td>
                                                <td className="py-1.5 text-right whitespace-nowrap">
                                                    {openQty > 0 && (
                                                        <button type="button" onClick={() => setWaiverServiceId(s.id)}
                                                            className="text-[11px] font-medium text-amber-600 hover:text-amber-800 underline underline-offset-2"
                                                            title="Auf die offene Restmenge mit Grund verzichten (der Leistungsstand bleibt).">
                                                            Mit Grund verzichten …
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {assessment && assessment.blockers.length > 0 && (
                            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 mb-4 flex gap-2">
                                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                                <div className="text-xs text-rose-900 leading-relaxed">
                                    <strong>Umstellung gesperrt:</strong> an den zu deckenden Positionen ist noch etwas offen.
                                    Zwei Wege: die Restmenge <strong>vorher abrechnen</strong>
                                    {onBillProject && (
                                        <button type="button" onClick={() => { onBillProject(project.id); onClose(); }}
                                            className="ml-1 inline-flex items-center gap-1 font-medium text-indigo-700 hover:text-indigo-900 underline underline-offset-2">
                                            zur Rechnungserstellung <ArrowRight className="w-3 h-3" />
                                        </button>
                                    )}
                                    {' '}oder je Position <strong>mit Grund verzichten</strong> (Spalte rechts).
                                </div>
                            </div>
                        )}

                        {assessment && assessment.errors.length > 0 && (
                            <ul className="rounded-xl border border-rose-200 bg-rose-50 p-3 mb-4 text-xs text-rose-900 list-disc list-inside space-y-1">
                                {assessment.errors.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        )}

                        {assessment && assessment.warnings.length > 0 && (
                            <ul className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-4 text-xs text-amber-900 list-disc list-inside space-y-1">
                                {assessment.warnings.map((w, i) => <li key={i}>{w}</li>)}
                            </ul>
                        )}

                        {kumulativHinweis && (
                            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-3 mb-4 text-xs text-indigo-900 leading-relaxed">
                                {kumulativHinweis}
                            </div>
                        )}

                        {assessment && assessment.ok && (
                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 mb-4 text-xs text-slate-600 leading-relaxed">
                                {assessment.lockedMonthKeys.length > 0 && (
                                    <p className="mb-1">
                                        {assessment.lockedMonthKeys.length} Monat(e) vor dem Stichtag werden als
                                        „je Einzelposition abgerechnet" verschlossen (ohne Betrag) — sie können nie als Pauschalmonat gefordert werden.
                                    </p>
                                )}
                                <p>
                                    <strong>Offen ab Stichtag:</strong>{' '}
                                    {assessment.openMonthKeys.length > 0
                                        ? `${assessment.openMonthKeys.map((k) => flatKeyLabel(k)).join(', ')} — nach der Umstellung über den Monatsvorschlag buchen, nie automatisch.`
                                        : 'noch kein abgeschlossener Monat.'}
                                </p>
                            </div>
                        )}

                        {fehler && <p className="text-sm text-rose-600 mb-3 whitespace-pre-line">{fehler}</p>}

                        <div className="flex justify-end gap-3">
                            <Button variant="secondary" onClick={onClose} disabled={laeuft}>Abbrechen</Button>
                            <Button variant="primary" onClick={umstellen}
                                disabled={laeuft || !assessment || !assessment.ok}
                                icon={laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}>
                                Jetzt umstellen
                            </Button>
                        </div>
                    </>
                )}
            </div>

            {waiverServiceId && (() => {
                const target = services.find((s) => s.id === waiverServiceId);
                if (!target) return null;
                return (
                    <ServiceWaiverDialog
                        service={target}
                        serviceLog={getServiceLog({ metadata: meta })}
                        onWaive={handleWaive}
                        onRelease={handleRelease}
                        onClose={() => setWaiverServiceId(null)}
                    />
                );
            })()}
        </div>
    );
};
